/**
 * Typed client-safe environment configuration.
 *
 * Safe to import from both server and client code. Only `NEXT_PUBLIC_*`
 * values live here. Next.js inlines those at build time, so validating
 * them in this module runs in both environments.
 *
 * For server-only values (secrets, DB URLs, OAuth client secrets, etc.)
 * import from `@/lib/env.server` — that module is tagged `server-only` so
 * Next.js will error if a client component accidentally imports it.
 *
 * All non-test, non-build-tooling access to `process.env.*` in `app/`,
 * `lib/`, and `components/` must go through `lib/env.ts` or
 * `lib/env.server.ts`. Direct `process.env.X` is flagged by ESLint.
 */

import { z } from 'zod';

const trimmedString = z.string().trim().min(1, 'required');

const optionalString = z
  .union([z.string(), z.undefined(), z.null()])
  .transform(v => {
    if (v === undefined || v === null) return undefined;
    const trimmed = v.trim();
    return trimmed.length === 0 ? undefined : trimmed;
  });

const optionalBoolean = z
  .union([z.string(), z.undefined(), z.null()])
  .transform(v => {
    if (!v) return undefined;
    const s = v.trim().toLowerCase();
    if (s === '1' || s === 'true' || s === 'yes' || s === 'on') return true;
    if (s === '0' || s === 'false' || s === 'no' || s === 'off') return false;
    return undefined;
  });

/**
 * Client schema — everything here is public and included in the browser
 * bundle. Only `NEXT_PUBLIC_*` names are allowed.
 */
const clientSchema = z.object({
  NEXT_PUBLIC_APP_URL: optionalString,
  NEXT_PUBLIC_BASE_URL: optionalString,
  NEXT_PUBLIC_SITE_URL: optionalString,
  NEXT_PUBLIC_SUPABASE_URL: trimmedString,
  NEXT_PUBLIC_SUPABASE_ANON_KEY: trimmedString,
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: trimmedString,
  NEXT_PUBLIC_CLERK_EXACT_ROLES: optionalString,
  NEXT_PUBLIC_DEV_AUTH_BYPASS: optionalBoolean,
  NEXT_PUBLIC_PLATFORM_OWNER_ORG_ID: optionalString,
  NEXT_PUBLIC_SENTRY_ENVIRONMENT: optionalString,
  NEXT_PUBLIC_SENTRY_DSN: optionalString,
  NEXT_PUBLIC_GA_MEASUREMENT_ID: optionalString,
  NEXT_PUBLIC_CLARITY_PROJECT_ID: optionalString,
  NEXT_PUBLIC_FRESHDESK_WIDGET_ENABLED: optionalBoolean,
  NEXT_PUBLIC_FRESHDESK_HOST: optionalString,
  NEXT_PUBLIC_FRESHDESK_TOKEN: optionalString,
  NEXT_PUBLIC_FRESHDESK_WIDGET_ID: optionalString,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: optionalString,
  NEXT_PUBLIC_GOOGLE_PLACES_API_KEY: optionalString,
  NEXT_PUBLIC_DISABLE_INVOICE_AUTOSAVE: optionalBoolean,
  NEXT_PUBLIC_DISABLE_ONBOARDING_AUTOSAVE: optionalBoolean,
  NEXT_PUBLIC_ADAPTIVE_TABLES: optionalBoolean,
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: optionalString,
  NEXT_PUBLIC_STRIPE_STARTER_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_STARTER_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_ANNUAL_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_LAUNCH_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_LAUNCH_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_STANDARD_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_PRO_STANDARD_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_ANNUAL_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_MONTHLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_YEARLY_PRICE_ID: optionalString,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF: optionalString,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: optionalString,
});

export type ClientEnv = z.infer<typeof clientSchema>;

// Keep every public value as a static property access. Next.js only inlines
// `NEXT_PUBLIC_*` variables into browser bundles when it can see the exact
// property name at build time; passing the whole `process.env` object through
// would make client-side reads resolve to `undefined`.
const rawProcessEnv = {
  NODE_ENV: process.env.NODE_ENV,
  VERCEL_ENV: process.env.VERCEL_ENV,
  ECLIPSE_ENV_STRICT: process.env.ECLIPSE_ENV_STRICT,
  NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
  NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
  NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
  NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
    process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
  NEXT_PUBLIC_CLERK_EXACT_ROLES: process.env.NEXT_PUBLIC_CLERK_EXACT_ROLES,
  NEXT_PUBLIC_DEV_AUTH_BYPASS: process.env.NEXT_PUBLIC_DEV_AUTH_BYPASS,
  NEXT_PUBLIC_PLATFORM_OWNER_ORG_ID:
    process.env.NEXT_PUBLIC_PLATFORM_OWNER_ORG_ID,
  NEXT_PUBLIC_SENTRY_ENVIRONMENT: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
  NEXT_PUBLIC_GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
  NEXT_PUBLIC_CLARITY_PROJECT_ID: process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID,
  NEXT_PUBLIC_FRESHDESK_WIDGET_ENABLED:
    process.env.NEXT_PUBLIC_FRESHDESK_WIDGET_ENABLED,
  NEXT_PUBLIC_FRESHDESK_HOST: process.env.NEXT_PUBLIC_FRESHDESK_HOST,
  NEXT_PUBLIC_FRESHDESK_TOKEN: process.env.NEXT_PUBLIC_FRESHDESK_TOKEN,
  NEXT_PUBLIC_FRESHDESK_WIDGET_ID: process.env.NEXT_PUBLIC_FRESHDESK_WIDGET_ID,
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
  NEXT_PUBLIC_GOOGLE_PLACES_API_KEY:
    process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
  NEXT_PUBLIC_DISABLE_INVOICE_AUTOSAVE:
    process.env.NEXT_PUBLIC_DISABLE_INVOICE_AUTOSAVE,
  NEXT_PUBLIC_DISABLE_ONBOARDING_AUTOSAVE:
    process.env.NEXT_PUBLIC_DISABLE_ONBOARDING_AUTOSAVE,
  NEXT_PUBLIC_ADAPTIVE_TABLES: process.env.NEXT_PUBLIC_ADAPTIVE_TABLES,
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  NEXT_PUBLIC_STRIPE_STARTER_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_STARTER_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_STARTER_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_STARTER_YEARLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_YEARLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_ANNUAL_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_ANNUAL_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_LAUNCH_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_LAUNCH_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_LAUNCH_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_LAUNCH_YEARLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_STANDARD_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_STANDARD_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_PRO_STANDARD_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_PRO_STANDARD_YEARLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_ANNUAL_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_ANNUAL_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_LAUNCH_YEARLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_MONTHLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_MONTHLY_PRICE_ID,
  NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_YEARLY_PRICE_ID:
    process.env.NEXT_PUBLIC_STRIPE_GROWTH_STANDARD_YEARLY_PRICE_ID,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF:
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF,
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA:
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
};

const isStrict =
  rawProcessEnv.VERCEL_ENV === 'production' ||
  rawProcessEnv.NODE_ENV === 'production' ||
  rawProcessEnv.ECLIPSE_ENV_STRICT === 'true';

function formatIssues(issues: z.ZodIssue[]): string {
  return issues
    .map(issue => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
    .join('\n');
}

function parseClient(): ClientEnv {
  const result = clientSchema.safeParse(rawProcessEnv);
  if (result.success) return result.data;

  const message = `Invalid client environment:\n${formatIssues(
    result.error.issues
  )}`;

  if (isStrict) {
    throw new Error(message);
  }

  if (typeof window === 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      `[env] Non-strict mode: ${result.error.issues.length} client env validation issue(s). Run with ECLIPSE_ENV_STRICT=true to fail fast.`
    );
  }
  return clientSchema.partial().parse(rawProcessEnv) as ClientEnv;
}

export const clientEnv: ClientEnv = parseClient();

export const runtimeEnvironment = rawProcessEnv.NODE_ENV ?? 'development';

/**
 * Shared constants — these are used both server- and client-side and
 * don't touch secrets. Exposed here so callers don't need to branch
 * between `clientEnv` and `serverEnv` for common values.
 */
export const isProduction =
  runtimeEnvironment === 'production' ||
  rawProcessEnv.VERCEL_ENV === 'production';
export const isDevelopment = runtimeEnvironment === 'development';
